import { endpoints } from '@/utils/endpoints'
import { matchInquiryTypeToInquirySettings } from '@/utils/matchers'
import { tz } from '@/utils/tz'
import { defineStore } from 'pinia'
import { INQUIRY_ITEM, INQUIRY_TYPE, IOS_ERROR, SPOT } from './../types.d'

interface CustomWindow extends Window {
  webkit?: any
}

declare const window: CustomWindow

export const useAppStore = defineStore('app', () => {
  const authStore = useAuthStore()
  const date = ref()
  const spotInfo = ref()
  const hasEventPlanner = ref(false)
  const spotHopperEmbedded = ref(false)
  const spotHopperDesktopDetailsPage = ref(false)
  const inquiryFilter = ref<string | undefined>()
  const spotsForUser = ref<SPOT[]>()
  const isAdmin = ref(false)
  const isLoading = ref(false)
  const iosError = ref<IOS_ERROR>()
  const isTablet = ref(false)
  const appReady = ref(false)
  let spotsForUserPromise: Promise<any> | null = null

  function setSpotTz(value: string) {
    date.value = tz(value)
  }

  function setSpotHopperEmbedded(value: {
    embedded: boolean
    desktopDetailsPage: boolean
    isTablet: boolean
  }) {
    spotHopperEmbedded.value = value.embedded
    spotHopperDesktopDetailsPage.value = value.desktopDetailsPage
    isTablet.value = value.isTablet
  }

  function setInquiryFilter(value: string) {
    inquiryFilter.value = value
  }

  function setSpotInfo(value: string) {
    spotInfo.value = value
    appReady.value = true
    hasEventPlanner.value =
      spotInfo.value.upgrade_status.event_planner_upgrade == 'subscribed'
  }

  function hasAutoResponseEnabled(inquiry: INQUIRY_ITEM) {
    return spotInfo.value[matchInquiryTypeToInquirySettings(inquiry.type)]
      ?.autoresponse_enabled
  }

  function autoResponseSentIn(inquiry: INQUIRY_ITEM) {
    //need to calculate the remaining time for the autoresponse

    return inquiry.autoresponse_exact_remaining_time
  }

  function matchSettingsTimeStep(inquiryType: INQUIRY_TYPE | undefined) {
    if (inquiryType == 'Private Party')
      return spotInfo.value.private_parties_settings.time_step
    if (inquiryType == 'Catering')
      return spotInfo.value.catering_settings.time_step
    if (inquiryType == 'Reservation')
      return spotInfo.value.reservation_settings.time_step

    return 30
  }

  function setSpotsForUser(spots: SPOT[]) {
    spotsForUser.value = spots.map((spot) => {
      return {
        name: spot.name,
        id: spot.id,
        selected: Number(authStore.spotId) == spot.id,
        notificationsEnabled:
          spot.app_notifications_toggled as SPOT['notificationsEnabled'],
      }
    })
  }

  async function toggleNotifications(
    spotId: number,
    toggle: boolean,
    toggle_name: string,
    inquiry_type: string,
  ) {
    await useApi(`${endpoints().toggleSpotNotifications(authStore.spotId)}`)
      .post({
        spot_id: spotId,
        toggle_value: toggle,
        device_id: localStorage.getItem('device-token'),
        toggle_name,
        inquiry_type,
      })
      .json()
  }

  function setIsAdmin(admin: boolean) {
    isAdmin.value = admin
  }

  function setIOSError(error: IOS_ERROR) {
    iosError.value = error
  }

  function sendErrorToiOS() {
    if (
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.shErrorHandler
    ) {
      window.webkit.messageHandlers.shErrorHandler.postMessage(
        JSON.stringify(iosError.value),
      )
    }
  }

  async function fetchSpotsForUser(deviceToken?: string) {
    if (spotsForUserPromise) return spotsForUserPromise

    const spotsUrl = deviceToken
      ? `${import.meta.env.VITE_SH_URL}/api/webapp/spots_for_user?device_id=${deviceToken}`
      : `${import.meta.env.VITE_SH_URL}/api/webapp/spots_for_user`

    spotsForUserPromise = Promise.resolve(useApi(spotsUrl).json()).then((res) => {
      spotsForUserPromise = null
      return res
    })

    return spotsForUserPromise
  }

  return {
    date,
    spotInfo,
    setSpotTz,
    setSpotInfo,
    hasEventPlanner,
    hasAutoResponseEnabled,
    autoResponseSentIn,
    matchSettingsTimeStep,
    spotHopperEmbedded,
    setSpotHopperEmbedded,
    spotHopperDesktopDetailsPage,
    setInquiryFilter,
    inquiryFilter,
    spotsForUser,
    setSpotsForUser,
    setIsAdmin,
    isAdmin,
    isLoading,
    toggleNotifications,
    iosError,
    setIOSError,
    sendErrorToiOS,
    isTablet,
    appReady,
    fetchSpotsForUser
  }
})
